type GraphQLDate = string | null;
type CompanyInfo = {
  id: string;
  host: string;
  plan: string;
  subscriptionId: string | null;
  createdAt: GraphQLDate;
};
type UserInfo = {
  id: string;
  name: string;
  email: string;
  title: string;
  type: string;
  avatar: string | null;
  createdAt: GraphQLDate;
  allowAccountAccess: boolean;
  company: CompanyInfo;
};

const dateToTimestamp = (date: GraphQLDate) =>
  date ? Math.floor(new Date(date).valueOf() / 1000) : null;

const normalizeCreatedAtField = <T extends { createdAt: GraphQLDate }>(
  obj: T,
) => {
  const { createdAt, ...rest } = obj;
  return {
    ...rest,
    created_at: dateToTimestamp(obj.createdAt),
  };
};

// Normalize Segment identify payload to Intercom formats
// Inspired by https://github.com/segmentio/analytics.js-integrations/blob/c851772d7c652937c6c1e65beae137df09804246/integrations/intercom/lib/index.js#L108-L121
const normalizeUserInfo = <T extends UserInfo>(userInfo: T) => ({
  ...normalizeCreatedAtField(userInfo),
  company: normalizeCreatedAtField(userInfo.company),
});

const {
  __slab: { INTERCOM_APP_ID, CSP_NONCE },
} = window;

const DELAY_FOR_BETTER_FCP = 3000;
const LAST_INTERCOM_OPEN_TIMESTAMP = 'lastIntercomOpenTimestamp';
const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;

const shouldLoadImmediately = () => {
  const lastOpenTimestamp = parseInt(
    localStorage.getItem(LAST_INTERCOM_OPEN_TIMESTAMP) ?? '0',
    10,
  );
  return lastOpenTimestamp > Date.now() - TWO_WEEKS_IN_MS;
};

class IntercomFacade {
  private readonly baseSettings: Record<string, unknown> = {
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: true,
  };

  isLoaded = false;

  errorCallback?: () => void = undefined;

  hasError = !INTERCOM_APP_ID;

  constructor() {
    // Provided by Intercom
    // https://developers.intercom.com/installing-intercom/web/installation/
    const shimmedIntercom = (...args: unknown[]) => {
      shimmedIntercom.c(args);

      if (args[0] === 'show') {
        if (!this.isLoaded) {
          this.load();
        }

        localStorage.setItem(
          LAST_INTERCOM_OPEN_TIMESTAMP,
          Date.now().toString(),
        );
      }
    };
    shimmedIntercom.q = [] as unknown[][];
    shimmedIntercom.c = (args: unknown[]) => {
      shimmedIntercom.q.push(args);
    };

    window.Intercom = shimmedIntercom;
    window.Intercom('boot', this.baseSettings);

    if (shouldLoadImmediately()) {
      setTimeout(() => {
        this.load();
      }, DELAY_FOR_BETTER_FCP);
    }
  }

  onError(callback: () => void) {
    if (!shouldLoadImmediately()) {
      this.errorCallback = callback;
    }
  }

  identify(userInfo: UserInfo, intercomHash: string) {
    const { avatar, ...user } = userInfo;
    window.Intercom?.('update', {
      ...this.baseSettings,
      ...normalizeUserInfo(userInfo),
      user_id: user.id,
      user_hash: intercomHash,
      avatar: avatar ? { type: 'avatar', image_url: avatar } : undefined,
    });
  }

  private load() {
    if (!INTERCOM_APP_ID) {
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.nonce = CSP_NONCE;
    script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
    document.body.appendChild(script);
    script.addEventListener('error', () => {
      this.hasError = true;
      this.errorCallback?.();
    });
    this.isLoaded = true;
  }
}

export default new IntercomFacade();
